import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  CheckCircle,
  Twitter,
  Network,
  TrendingUp,
  GitBranch,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ProductPageNav, ProductPageFooter } from '@/components/ProductPageNav'
import { SEOHead } from '@/components/SEOHead'

// ── SVG components ──────────────────────────────────────────────────────────

function AccountAnalysisSVG() {
  return (
    <svg viewBox="0 0 680 200" className="w-full max-w-3xl mx-auto" aria-label="Account analysis pipelines">
      {/* Profile card */}
      <rect x="10" y="30" width="130" height="140" rx="8" fill="#0d1117" stroke="#22c55e" strokeWidth="1.5" />
      <rect x="10" y="30" width="130" height="4" rx="2" fill="#22c55e" />
      {/* Avatar circle */}
      <circle cx="75" cy="78" r="24" fill="#0d1a0d" stroke="#22c55e" strokeWidth="1.5" />
      <text x="75" y="84" textAnchor="middle" fill="#22c55e" fontSize="16" fontWeight="700">@</text>
      <text x="75" y="116" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="600">@handle</text>
      <text x="75" y="130" textAnchor="middle" fill="#64748b" fontSize="9">12.4K followers</text>
      <text x="75" y="143" textAnchor="middle" fill="#64748b" fontSize="9">890 following</text>
      <text x="75" y="158" textAnchor="middle" fill="#475569" fontSize="8">joined 2019</text>

      {/* Dashed connectors — branch lines from profile to outputs */}
      {/* Branch 1 — blue — Posting Cadence (top) */}
      <line x1="140" y1="65" x2="200" y2="48" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      <line x1="200" y1="48" x2="245" y2="48" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      {/* Branch 2 — green — Engagement Rate */}
      <line x1="140" y1="85" x2="200" y2="88" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      <line x1="200" y1="88" x2="245" y2="88" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      {/* Branch 3 — amber — Language Mix */}
      <line x1="140" y1="115" x2="200" y2="128" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      <line x1="200" y1="128" x2="245" y2="128" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      {/* Branch 4 — red — Bot Score */}
      <line x1="140" y1="148" x2="200" y2="162" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />
      <line x1="200" y1="162" x2="245" y2="162" stroke="#1e3a1e" strokeWidth="1.5" strokeDasharray="6 4" />

      {/* Output box 1 — Posting Cadence (blue) */}
      <rect x="245" y="18" width="190" height="60" rx="7" fill="#0d1117" stroke="#3b82f6" strokeWidth="1.5" />
      <text x="258" y="35" fill="#3b82f6" fontSize="10" fontWeight="700">Posting Cadence</text>
      {/* Mini bar chart — 5 bars */}
      {[18, 32, 14, 42, 26].map((h, i) => (
        <rect key={i} x={262 + i * 22} y={68 - h} width="14" height={h} rx="2" fill="#3b82f650" stroke="#3b82f6" strokeWidth="0.8" />
      ))}
      <text x="258" y="72" fill="#64748b" fontSize="8">Mon–Fri activity</text>

      {/* Output box 2 — Engagement Rate (green) */}
      <rect x="245" y="58" width="190" height="60" rx="7" fill="#0d1117" stroke="#22c55e" strokeWidth="1.5" />
      <text x="258" y="75" fill="#22c55e" fontSize="10" fontWeight="700">Engagement Rate</text>
      {/* Donut arc */}
      <circle cx="390" cy="86" r="16" fill="none" stroke="#1e2d1e" strokeWidth="5" />
      <circle cx="390" cy="86" r="16" fill="none" stroke="#22c55e" strokeWidth="5"
        strokeDasharray="60 40" strokeLinecap="round" transform="rotate(-90 390 86)" />
      <text x="390" y="90" textAnchor="middle" fill="#22c55e" fontSize="8" fontWeight="700">4.2%</text>
      <text x="258" y="110" fill="#64748b" fontSize="8">avg. per tweet</text>

      {/* Output box 3 — Language Mix (amber) */}
      <rect x="245" y="98" width="190" height="60" rx="7" fill="#0d1117" stroke="#f59e0b" strokeWidth="1.5" />
      <text x="258" y="115" fill="#f59e0b" fontSize="10" fontWeight="700">Language Mix</text>
      <rect x="258" y="120" width="70" height="12" rx="2" fill="#3b82f660" />
      <text x="262" y="130" fill="#e2e8f0" fontSize="8">EN 58%</text>
      <rect x="333" y="120" width="40" height="12" rx="2" fill="#f59e0b60" />
      <text x="337" y="130" fill="#e2e8f0" fontSize="8">HI 33%</text>
      <rect x="378" y="120" width="20" height="12" rx="2" fill="#8b5cf660" />
      <text x="381" y="130" fill="#e2e8f0" fontSize="8">AR 9%</text>
      <text x="258" y="150" fill="#64748b" fontSize="8">3 languages detected</text>

      {/* Output box 4 — Bot Score (red) */}
      <rect x="245" y="138" width="190" height="50" rx="7" fill="#0d1117" stroke="#ef4444" strokeWidth="1.5" />
      <text x="258" y="155" fill="#ef4444" fontSize="10" fontWeight="700">Bot Score: 23</text>
      {/* Small gauge arc */}
      <path d="M 375 175 A 18 18 0 0 1 415 175" fill="none" stroke="#1e2d1e" strokeWidth="5" strokeLinecap="round" />
      <path d="M 375 175 A 18 18 0 0 1 384 158" fill="none" stroke="#22c55e" strokeWidth="5" strokeLinecap="round" />
      <text x="395" y="174" fill="#22c55e" fontSize="8" fontWeight="600">LOW</text>
      <text x="258" y="180" fill="#64748b" fontSize="8">14-signal model</text>

      {/* Right panel — feature summary */}
      <rect x="455" y="18" width="215" height="170" rx="8" fill="#0d1117" stroke="#1e3a1e" strokeWidth="1" />
      <text x="470" y="38" fill="#e2e8f0" fontSize="11" fontWeight="700">Account Intelligence</text>
      {[
        { label: 'Follower graph mapping', y: 58 },
        { label: 'Posting pattern anomalies', y: 76 },
        { label: 'Engagement authenticity score', y: 94 },
        { label: 'Geo-language distribution', y: 112 },
        { label: 'Account age vs activity', y: 130 },
        { label: 'Bot probability: 0–100', y: 148 },
        { label: 'Coordinated behavior flags', y: 166 },
      ].map((item) => (
        <g key={item.y}>
          <circle cx="472" cy={item.y - 3} r="3" fill="#22c55e" />
          <text x="482" y={item.y} fill="#94a3b8" fontSize="9">{item.label}</text>
        </g>
      ))}
    </svg>
  )
}

function NetworkGraphSVG() {
  const target = { x: 300, y: 140, r: 22 }
  const outer = [
    { x: 160, y: 68,  r: 14, bot: false, color: '#3b82f6' },
    { x: 300, y: 45,  r: 17, bot: false, color: '#3b82f6' },
    { x: 430, y: 60,  r: 12, bot: true,  color: '#ef4444' },
    { x: 490, y: 155, r: 16, bot: false, color: '#3b82f6' },
    { x: 440, y: 230, r: 11, bot: true,  color: '#ef4444' },
    { x: 300, y: 255, r: 14, bot: false, color: '#3b82f6' },
    { x: 155, y: 228, r: 13, bot: false, color: '#3b82f6' },
    { x: 100, y: 148, r: 18, bot: true,  color: '#ef4444' },
  ]
  // Second-degree mini-nodes from nodes 0 and 7
  const second = [
    { x: 68,  y: 38,  from: 0 },
    { x: 100, y: 68,  from: 0 },
    { x: 42,  y: 118, from: 7 },
    { x: 52,  y: 185, from: 7 },
  ]
  // Sockpuppet cluster: nodes 2 and 4 (indices)
  const spLink = { from: outer[2], to: outer[4] }

  return (
    <svg viewBox="0 0 600 280" className="w-full max-w-2xl mx-auto" aria-label="Network graph">
      {/* Sockpuppet amber boundary oval */}
      <ellipse cx="460" cy="155" rx="60" ry="90" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="8 4" opacity="0.7" />
      <text x="510" y="100" fill="#f59e0b" fontSize="9">Sock puppet cluster</text>

      {/* Edges from target to outer nodes */}
      {outer.map((n, i) => (
        <line key={i}
          x1={target.x} y1={target.y}
          x2={n.x} y2={n.y}
          stroke="#1e3a2a" strokeWidth={n.r > 14 ? 1.8 : 1}
          opacity={n.r > 14 ? 0.9 : 0.5}
        />
      ))}
      {/* Sockpuppet internal link */}
      <line x1={spLink.from.x} y1={spLink.from.y} x2={spLink.to.x} y2={spLink.to.y}
        stroke="#f59e0b" strokeWidth="1.2" strokeDasharray="5 3" opacity="0.8" />

      {/* Second-degree nodes and their edges */}
      {second.map((s, i) => {
        const parent = outer[s.from]
        return (
          <g key={`sec-${i}`}>
            <line x1={parent.x} y1={parent.y} x2={s.x} y2={s.y}
              stroke="#1e3a2a" strokeWidth="0.8" strokeDasharray="4 3" opacity="0.4" />
            <circle cx={s.x} cy={s.y} r="7" fill="#0d1117" stroke="#3b82f650" strokeWidth="1" />
          </g>
        )
      })}

      {/* Outer nodes */}
      {outer.map((n, i) => (
        <g key={`n-${i}`}>
          {n.bot && (
            <circle cx={n.x} cy={n.y} r={n.r + 6} fill="none" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.6" />
          )}
          <circle cx={n.x} cy={n.y} r={n.r} fill="#0d1117" stroke={n.color} strokeWidth="1.5" />
          <circle cx={n.x} cy={n.y} r={n.r - 5} fill={`${n.color}18`} />
          {n.bot && <text x={n.x} y={n.y + 4} textAnchor="middle" fill="#ef4444" fontSize="7" fontWeight="700">BOT?</text>}
        </g>
      ))}

      {/* Target node */}
      <circle cx={target.x} cy={target.y} r={target.r + 8} fill="none" stroke="#3b82f630" strokeWidth="3" />
      <circle cx={target.x} cy={target.y} r={target.r} fill="#0d1117" stroke="#3b82f6" strokeWidth="2.5" />
      <circle cx={target.x} cy={target.y} r={target.r - 7} fill="#3b82f618" />
      <text x={target.x} y={target.y + 4} textAnchor="middle" fill="#3b82f6" fontSize="9" fontWeight="700">TARGET</text>

      {/* Legend */}
      <rect x="8" y="240" width="170" height="35" rx="5" fill="#0d1117" stroke="#1e3a1e" strokeWidth="1" />
      <circle cx="22" cy="252" r="5" fill="#0d1117" stroke="#3b82f6" strokeWidth="1.5" />
      <text x="32" y="256" fill="#94a3b8" fontSize="9">User node (size = followers)</text>
      <circle cx="22" cy="268" r="5" fill="#0d1117" stroke="#ef4444" strokeWidth="1.5" />
      <circle cx="22" cy="268" r="9" fill="none" stroke="#ef4444" strokeWidth="1" strokeDasharray="3 2" />
      <text x="32" y="272" fill="#94a3b8" fontSize="9">Bot suspected</text>
    </svg>
  )
}

function BotScoreSVG() {
  const score = 23
  const radius = 60
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  const signals = [
    { label: 'Account Age',         risk: 'LOW',  fill: '#22c55e', pct: 0.12 },
    { label: 'Follower Ratio',      risk: 'LOW',  fill: '#22c55e', pct: 0.18 },
    { label: 'Posting Rate',        risk: 'MED',  fill: '#f59e0b', pct: 0.48 },
    { label: 'Name Entropy',        risk: 'LOW',  fill: '#22c55e', pct: 0.08 },
    { label: 'Profile Completeness', risk: 'LOW', fill: '#22c55e', pct: 0.06 },
    { label: 'Network Density',     risk: 'MED',  fill: '#f59e0b', pct: 0.42 },
  ]
  const barW = 200
  const barH = 10
  const startX = 270
  const startY = 42

  return (
    <svg viewBox="0 0 500 220" className="w-full max-w-xl mx-auto" aria-label="Bot score gauge">
      {/* Gauge background */}
      <circle cx="112" cy="115" r={radius} fill="none" stroke="#1e2d1e" strokeWidth="12" />
      <circle cx="112" cy="115" r={radius}
        fill="none" stroke="#22c55e" strokeWidth="12"
        strokeDasharray={`${circumference}`}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 112 115)"
      />
      <text x="112" y="108" textAnchor="middle" fill="#22c55e" fontSize="30" fontWeight="800">{score}</text>
      <text x="112" y="126" textAnchor="middle" fill="#94a3b8" fontSize="10">/100 bot score</text>
      <text x="112" y="144" textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="700">LOW PROBABILITY</text>

      {/* Gauge labels */}
      <text x="54" y="186" fill="#22c55e" fontSize="8">LOW</text>
      <text x="100" y="186" fill="#f59e0b" fontSize="8">MED</text>
      <text x="145" y="186" fill="#ef4444" fontSize="8">HIGH</text>
      <path d="M 58 180 Q 112 155 166 180" fill="none" stroke="#334155" strokeWidth="1" />

      {/* Divider */}
      <line x1="218" y1="20" x2="218" y2="200" stroke="#1e3a1e" strokeWidth="1" />

      {/* Signal bars */}
      <text x={startX} y="25" fill="#e2e8f0" fontSize="11" fontWeight="700">Signal Analysis</text>
      {signals.map((s, i) => {
        const y = startY + i * 29
        return (
          <g key={s.label}>
            <text x={startX} y={y + 10} fill="#e2e8f0" fontSize="10" fontWeight="500">{s.label}</text>
            <text x={startX + barW + 4} y={y + 10} fill={s.fill} fontSize="9" fontWeight="700">{s.risk}</text>
            {/* Bar track */}
            <rect x={startX} y={y + 14} width={barW} height={barH} rx="4" fill="#0d1117" stroke="#1e3a1e" strokeWidth="1" />
            {/* Bar fill */}
            <rect x={startX} y={y + 14} width={barW * s.pct} height={barH} rx="4" fill={s.fill} opacity="0.85" />
          </g>
        )
      })}
    </svg>
  )
}

function TweetCascadeSVG() {
  // Root tweet at left, branches to 3 retweets, then quote tweets
  const root = { x: 40, y: 100, w: 100, h: 40 }
  const rts = [
    { x: 195, y: 38,  w: 105, h: 36, ts: '0m',  coordinated: false },
    { x: 195, y: 95,  w: 105, h: 36, ts: '4m',  coordinated: false },
    { x: 195, y: 152, w: 105, h: 36, ts: '7m',  coordinated: true  },
  ]
  const quotes = [
    { x: 360, y: 20,  w: 95, h: 30, parent: 0, ts: '2m'  },
    { x: 360, y: 60,  w: 95, h: 30, parent: 0, ts: '9m'  },
    { x: 360, y: 90,  w: 95, h: 30, parent: 1, ts: '5m'  },
    { x: 360, y: 128, w: 95, h: 30, parent: 1, ts: '11m' },
    { x: 360, y: 160, w: 95, h: 30, parent: 2, ts: '8m'  },
    { x: 360, y: 195, w: 95, h: 30, parent: 2, ts: '8m'  },
  ]

  return (
    <svg viewBox="0 0 660 200" className="w-full max-w-3xl mx-auto" aria-label="Tweet cascade">
      {/* Root tweet */}
      <rect x={root.x} y={root.y} width={root.w} height={root.h} rx="7"
        fill="#0d1117" stroke="#22c55e" strokeWidth="2" />
      <text x={root.x + root.w / 2} y={root.y + 14} textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="700">Root Tweet</text>
      <text x={root.x + root.w / 2} y={root.y + 28} textAnchor="middle" fill="#64748b" fontSize="8">t = 0:00</text>

      {/* Lines root → RTs */}
      {rts.map((rt, i) => (
        <line key={`line-rt-${i}`}
          x1={root.x + root.w} y1={root.y + root.h / 2}
          x2={rt.x} y2={rt.y + rt.h / 2}
          stroke={rt.coordinated ? '#ef444480' : '#1e3a1e'} strokeWidth="1.5" />
      ))}

      {/* Retweet nodes */}
      {rts.map((rt, i) => (
        <g key={`rt-${i}`}>
          <rect x={rt.x} y={rt.y} width={rt.w} height={rt.h} rx="6"
            fill="#0d1117" stroke={rt.coordinated ? '#ef4444' : '#3b82f6'} strokeWidth={rt.coordinated ? 2 : 1.5} />
          <text x={rt.x + rt.w / 2} y={rt.y + 13} textAnchor="middle"
            fill={rt.coordinated ? '#ef4444' : '#3b82f6'} fontSize="9" fontWeight="600">
            {rt.coordinated ? 'RT (coordinated)' : 'Retweet'}
          </text>
          <text x={rt.x + rt.w / 2} y={rt.y + 26} textAnchor="middle" fill="#64748b" fontSize="8">+{rt.ts}</text>
          {rt.coordinated && (
            <text x={rt.x + rt.w / 2} y={rt.y + rt.h + 10} textAnchor="middle" fill="#ef4444" fontSize="8" fontWeight="700">
              AMPLIFIED
            </text>
          )}
        </g>
      ))}

      {/* Lines RTs → Quotes */}
      {quotes.map((q, i) => {
        const parent = rts[q.parent]
        return (
          <line key={`line-q-${i}`}
            x1={parent.x + parent.w} y1={parent.y + parent.h / 2}
            x2={q.x} y2={q.y + q.h / 2}
            stroke={parent.coordinated ? '#ef444450' : '#1e3a1e'} strokeWidth="1" strokeDasharray="4 3" />
        )
      })}

      {/* Quote tweet nodes */}
      {quotes.map((q, i) => {
        const parent = rts[q.parent]
        return (
          <g key={`q-${i}`}>
            <rect x={q.x} y={q.y} width={q.w} height={q.h} rx="5"
              fill="#0d1117" stroke={parent.coordinated ? '#ef444470' : '#8b5cf660'} strokeWidth="1" />
            <text x={q.x + q.w / 2} y={q.y + 12} textAnchor="middle"
              fill={parent.coordinated ? '#ef4444' : '#8b5cf6'} fontSize="8" fontWeight="600">Quote</text>
            <text x={q.x + q.w / 2} y={q.y + 23} textAnchor="middle" fill="#64748b" fontSize="7">+{q.ts}</text>
          </g>
        )
      })}

      {/* Legend */}
      <rect x="472" y="148" width="175" height="48" rx="6" fill="#0d1117" stroke="#1e3a1e" strokeWidth="1" />
      <line x1="482" y1="162" x2="500" y2="162" stroke="#3b82f6" strokeWidth="2" />
      <text x="506" y="166" fill="#94a3b8" fontSize="9">Normal retweet</text>
      <line x1="482" y1="178" x2="500" y2="178" stroke="#ef4444" strokeWidth="2" />
      <text x="506" y="182" fill="#94a3b8" fontSize="9">Coordinated amplification</text>
    </svg>
  )
}

function NarrativeTimelineSVG() {
  // Area chart with spike
  const points = [
    [0, 90], [30, 85], [60, 80], [90, 75], [120, 70],
    [150, 68], [180, 72], [210, 65], [240, 40], [270, 12],
    [285, 5], [300, 15], [330, 55], [360, 68], [390, 72],
    [420, 76], [450, 80],
  ]
  const pathD = points.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(' ')
  const areaD = `${pathD} L 450 110 L 0 110 Z`

  return (
    <svg viewBox="0 0 470 120" className="w-full max-w-lg" aria-label="Narrative timeline frequency">
      {/* Grid lines */}
      {[20, 40, 60, 80, 100].map(y => (
        <line key={y} x1="0" y1={y} x2="450" y2={y} stroke="#1e3a1e" strokeWidth="0.5" />
      ))}
      {/* Area fill */}
      <path d={areaD} fill="#22c55e18" />
      {/* Line */}
      <path d={pathD} fill="none" stroke="#22c55e" strokeWidth="2" />
      {/* Spike annotation */}
      <line x1="285" y1="5" x2="285" y2="115" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 3" />
      <rect x="220" y="2" width="128" height="16" rx="4" fill="#0d1117" stroke="#f59e0b" strokeWidth="1" />
      <text x="284" y="13" textAnchor="middle" fill="#f59e0b" fontSize="9" fontWeight="700">Coordinated boost</text>
      {/* Axis labels */}
      <text x="0" y="118" fill="#64748b" fontSize="8">Day 0</text>
      <text x="200" y="118" fill="#64748b" fontSize="8">Day 14</text>
      <text x="420" y="118" fill="#64748b" fontSize="8">Day 30</text>
      <text x="-2" y="14" fill="#64748b" fontSize="8" writingMode="vertical-lr">Freq</text>
    </svg>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function XInvestigationProduct() {
  const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
  }

  return (
    <div className="min-h-screen bg-[#0a0f0a] text-white overflow-x-hidden">
      <SEOHead
        title="X / Twitter Investigation — Deep Account & Event Analysis | Sycek OSINT"
        description="Profile any Twitter/X account or post in depth. Follower graph mapping, sentiment timeline, bot detection, engagement scoring, network influence analysis, and AI-generated investigation briefs."
        keywords="Twitter investigation, X investigation, Twitter OSINT, social media investigation, follower graph analysis, bot detection, Twitter account analysis, sentiment analysis, social network analysis, influence mapping, Twitter intelligence, X profile lookup"
        canonical="/x-investigation-product"
        jsonLd={{
          "@type": "Product",
          "name": "X / Twitter Investigation",
          "description": "Deep account and event investigation for Twitter/X. Follower graphs, sentiment analysis, bot scoring, engagement metrics, and AI investigation briefs.",
          "brand": { "@type": "Brand", "name": "Sycek OSINT" },
          "url": "https://sycek.io/x-investigation-product",
          "category": "Security Software"
        }}
      />

      {/* Background grid */}
      <div
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(34,197,94,0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,197,94,0.15) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />

      <ProductPageNav activePage="X Investigation" />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative z-10 pt-20 pb-16 px-6">
        <div className="container mx-auto max-w-5xl text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Badge className="mb-6 bg-green-500/10 text-green-400 border border-green-500/25 text-xs uppercase tracking-widest px-4 py-1.5">
              X Investigation — Twitter / X Intelligence
            </Badge>
          </motion.div>

          <motion.h1
            initial="hidden" animate="visible" custom={1} variants={fadeUp}
            className="text-5xl md:text-6xl font-bold leading-tight mb-6">
            <span className="text-white">Investigate Any</span>
            <br />
            <span className="text-green-400 drop-shadow-[0_0_30px_rgba(34,197,94,0.6)]">X Account or Event</span>
          </motion.h1>

          <motion.p
            initial="hidden" animate="visible" custom={2} variants={fadeUp}
            className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-10">
            Deep Twitter/X intelligence for investigating accounts, posts, and networks. Bot detection, retweet cascade analysis, network graph mapping, and narrative tracking — all in a single investigation session.
          </motion.p>

          <motion.div
            initial="hidden" animate="visible" custom={3} variants={fadeUp}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/app/x-investigation">
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 shadow-lg shadow-green-500/40">
                Open X Investigation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                Get Started
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-10 border-y border-white/[0.06] bg-white/[0.02]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500M+', label: 'Tweets/day indexed' },
              { value: '14-signal', label: 'Bot detection model' },
              { value: '10K nodes', label: 'Max network graph size' },
              { value: 'Real-time', label: 'Stream processing' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-bold text-green-400 mb-1">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Account Analysis ──────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <Badge className="mb-4 bg-blue-500/10 text-blue-400 border border-blue-500/25 text-xs uppercase tracking-widest">Account Analysis</Badge>
            <h2 className="text-3xl font-bold text-white mb-3">Full Profile Intelligence</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Every account investigation starts with a complete profile enrichment — follower graph, posting cadence, language distribution, engagement authenticity, and bot probability from a 14-signal model.
            </p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
            <AccountAnalysisSVG />
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { icon: Twitter,    color: 'text-blue-400',   title: 'Follower Graph',   body: 'Map the full follower/following relationship network with cluster detection.' },
              { icon: TrendingUp, color: 'text-green-400',  title: 'Posting Cadence',  body: 'Detect anomalous burst patterns, scheduled posting behavior, and bot-like regularity.' },
              { icon: Network,    color: 'text-amber-400',  title: 'Language Mix',     body: 'Identify multi-language accounts and cross-region coordinated amplification.' },
              { icon: GitBranch,  color: 'text-purple-400', title: 'Engagement Score', body: 'Measure authentic engagement rate against follower count to flag purchased followers.' },
            ].map((card) => (
              <div key={card.title} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4">
                <card.icon className={`w-5 h-5 ${card.color} mb-3`} />
                <div className="text-sm font-semibold text-white mb-1">{card.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{card.body}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Network Graph ─────────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Badge className="mb-4 bg-purple-500/10 text-purple-400 border border-purple-500/25 text-xs uppercase tracking-widest">Network Graph</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Visualize the Network</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Force-directed relationship graphs up to 10,000 nodes. Identify mutual follower clusters, detect potential sock puppet accounts, and map coordinated inauthentic behavior with visual cluster analysis.
              </p>
              <ul className="space-y-3">
                {[
                  'First and second-degree follower/following mapping',
                  'Bot-suspected nodes flagged with risk rings',
                  'Sock puppet cluster detection via mutual follow analysis',
                  'Interaction weight — heavier lines = more retweets/replies',
                  'Export network as JSON or Gephi-compatible format',
                  'Temporal graph — see how the network grew over time',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
              <NetworkGraphSVG />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Bot Score ─────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <Badge className="mb-4 bg-red-500/10 text-red-400 border border-red-500/25 text-xs uppercase tracking-widest">Bot Detection</Badge>
            <h2 className="text-3xl font-bold text-white mb-3">14-Signal Bot Probability Score</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our bot detection model weighs 14 independent signals — account age, follower ratio, posting rate, name entropy, profile completeness, network density, and more — into a single 0–100 probability score.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8">
            <BotScoreSVG />
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {[
              { range: '0–30', label: 'Low Bot Probability', color: 'border-green-500/30 bg-green-500/[0.04]', textColor: 'text-green-400', desc: 'Account exhibits primarily human behavioral patterns. Proceed with standard investigation.' },
              { range: '30–65', label: 'Medium — Review', color: 'border-amber-500/30 bg-amber-500/[0.04]', textColor: 'text-amber-400', desc: 'Mixed signals detected. Manual review recommended. Check posting schedule and follower quality.' },
              { range: '65–100', label: 'High Bot Probability', color: 'border-red-500/30 bg-red-500/[0.04]', textColor: 'text-red-400', desc: 'Strong bot or automation indicators. Likely coordinated inauthentic behavior. Escalate for deeper review.' },
            ].map((band) => (
              <div key={band.range} className={`border rounded-xl p-5 ${band.color}`}>
                <div className={`text-xl font-bold mb-1 ${band.textColor}`}>{band.range}</div>
                <div className="text-sm font-semibold text-white mb-2">{band.label}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{band.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Tweet Cascade ─────────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
              <TweetCascadeSVG />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border border-cyan-500/25 text-xs uppercase tracking-widest">XEvent Investigation</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Tweet Deep-Dive</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Investigate a single post — trace its full retweet cascade, quote-tweet tree, and detect coordinated amplification. See exactly who retweeted within the first 60 seconds and identify inauthentic burst patterns.
              </p>
              <ul className="space-y-3">
                {[
                  'Full retweet cascade with temporal spread visualization',
                  'Quote-tweet sentiment scoring per branch',
                  'Coordinated amplification detection — burst analysis',
                  'Account age of retweeters (detect bot farms)',
                  'Geographic spread of the cascade',
                  'Export cascade data as structured JSON',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Narrative Tracking ────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500/10 text-amber-400 border border-amber-500/25 text-xs uppercase tracking-widest">Narrative Tracking</Badge>
            <h2 className="text-3xl font-bold text-white mb-3">Track How Narratives Spread</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Monitor how a hashtag, keyword, or narrative propagates across the X network over time. Detect coordinated boosts, organic vs inorganic growth patterns, and origin accounts.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
              <NarrativeTimelineSVG />
              <p className="text-xs text-slate-600 text-center mt-3">Hashtag frequency timeline — spike indicates coordinated amplification event</p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}>
              <ul className="space-y-5">
                {[
                  { title: 'Hashtag Frequency Timeline', body: 'Plot tweet volume for any hashtag over a custom date range. Visual spikes signal coordinated injection events.' },
                  { title: 'Origin Account Tracing', body: 'Identify the first accounts to use a hashtag and trace how they are connected to each other.' },
                  { title: 'Organic vs Inorganic Growth', body: 'Distinguish natural adoption from artificial amplification using velocity and account-age analysis.' },
                  { title: 'Cross-Platform Spread', body: 'See when a narrative moves from X to Telegram, Reddit, or news media — with source linkage.' },
                  { title: 'Influence Attribution', body: 'Rank accounts by their contribution to narrative spread — not just follower count but cascade depth.' },
                ].map((item) => (
                  <li key={item.title} className="flex items-start gap-4">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-semibold text-white mb-0.5">{item.title}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{item.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Capabilities grid ─────────────────────────────────────────────── */}
      <section className="relative z-10 py-20 px-6 bg-white/[0.015]">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">Everything You Need to Investigate X</h2>
            <p className="text-slate-400 max-w-xl mx-auto">A complete toolkit from account profiling to post-level cascade analysis.</p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Account Deep-Dive',    desc: 'Full profile enrichment: follower graph, cadence, language, engagement, anomalies, and bot score.' },
              { title: 'Bot Score 0–100',      desc: '14-signal model: account age, follower ratio, posting rate, name entropy, network density, and more.' },
              { title: 'Network Graph',        desc: 'Force-directed graph of follower/following relationships. Cluster analysis, sock puppet detection.' },
              { title: 'Retweet Cascade',      desc: 'Visualize the full spread of any tweet — who amplified it, when, and how fast.' },
              { title: 'Quote-Tweet Analysis', desc: 'Sentiment scoring of quote tweets per branch. Flag hostile or coordinated quote campaigns.' },
              { title: 'Narrative Timeline',   desc: 'Track hashtag frequency over time with coordinated boost detection and origin attribution.' },
            ].map((f) => (
              <div key={f.title} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-green-500/20 transition-colors">
                <div className="text-sm font-semibold text-white mb-2">{f.title}</div>
                <div className="text-xs text-slate-500 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="relative z-10 py-24 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-4xl font-bold text-white mb-4">Start Your X Investigation</h2>
            <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              From a username to a full network map and bot assessment in minutes. Sign up and run your first investigation free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-semibold px-10 shadow-lg shadow-green-500/40">
                  Start Investigating
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app/x-investigation">
                <Button size="lg" variant="outline" className="border-white/[0.15] text-slate-300 hover:text-white hover:bg-white/[0.05]">
                  Open X Investigation
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <ProductPageFooter />
    </div>
  )
}
